import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppEnums } from 'src/app/enums/app.enum';
import { ApiResponse } from 'src/app/models/api-response.interface';
import { AppService } from 'src/app/services/app.service';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-company-info-partial',
  templateUrl: './company-info-partial.component.html',
  styleUrls: ['./company-info-partial.component.scss'],
})
export class CompanyInfoPartialComponent implements OnInit {
  @ViewChild('stepper') private myStepper: MatStepper;
  @Input() receivedId: string;
  @Input() selectedQuoteId: string;
  @Input() product: string;
  @Input() currentRecordQuestionsId: string;
  @Output() companyInfoEvent: EventEmitter<any> = new EventEmitter<any>();
  companyFormGroup: FormGroup;
  companyMultiUploadFormGroup: FormGroup;
  allowedFileExtentions = ['jpg', 'jpeg', 'png', 'pdf'];
  azureFileLocation: string = 'manual-sales';
  activeFileName: string = '';
  activeFile: any;
  _tstamp: number = 0;
  isLoading: boolean = false;
  currentRecord: any;
  companyFiles: any[] = [
    { name: 'KRA PIN Document', value: 'kraPhoto', suffix: '_KRA_PIN' },
    {
      name: 'Incorporation Certificate',
      value: 'incorporationCert',
      suffix: '_INCORPORATION_CERT',
    },
  ];

  constructor(
    private _formBuilder: FormBuilder,
    private appService: AppService,
    private router: Router
  ) {
    this._tstamp = Date.now();
    this.companyFormGroup = this._formBuilder.group({
      emailAddress: [
        '',
        Validators.compose([Validators.required, Validators.email]),
      ],
      name: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      kraPin: ['', Validators.required],
      KraDoc: ['', Validators.required],
      incorporationCert: ['', Validators.required],
      physicaladdress: ['', Validators.required],
      postalAddress: ['', Validators.required],
      postalCode: ['', Validators.required],
    });
    this.companyMultiUploadFormGroup = _formBuilder.group({
      fileObj: ['', Validators.required],
    });
  }

  onCompanyFileObjChange(idx: number) {
    this.activeFile = this.companyFiles[idx];
    this.activeFileName = this._tstamp + this.activeFile.suffix;
  }

  uploadCompanyDoc($event) {
    switch (this.activeFile.value) {
      case 'kraPhoto':
        this.companyFormGroup.patchValue({
          KraDoc: $event,
        });
        break;
      case 'incorporationCert':
        this.companyFormGroup.patchValue({
          incorporationCert: $event,
        });
        break;
      default:
        break;
    }
  }

  validateCompany() {
    if (!this.companyFormGroup.valid) {
      const controls = this.companyFormGroup.controls;
      for (const name in controls) {
        if (controls[name].invalid) {
          this.appService.showToastMessage(
            AppEnums.ToastTypeWarning,
            'Field Required',
            name
          );
          break;
        }
      }
      let email = this.companyFormGroup.get('emailAddress').value;
      if (!this.appService.isValidEmail(email)) {
        this.appService.showToastMessage(
          AppEnums.ToastTypeWarning,
          'Invalid email address',
          email
        );
      }

      return;
    }
    this.companyInfoEvent.emit(this.companyFormGroup.value);
  }

  updateCompany() {
    this.isLoading = true;

    let moreQuestions = {
      _id: this.currentRecordQuestionsId,
      update: {
        moreQuestions: {
          company: this.companyFormGroup.value,
        },
      },
    };

    this.appService
      .makePostRequest(
        `${environment.MOTOR_API_URL}/adminUpdateQuestionsGeneral`,
        moreQuestions
      )
      .subscribe(
        (apiResponse) => {
          this.isLoading = false;
          if (apiResponse.Status === 200) {
            this.appService.showToastMessage(
              AppEnums.ToastTypeSuccess,
              'Success!',
              'Quote saved successfully!.'
            );
          } else {
            this.appService.showToastMessage(
              AppEnums.ToastTypeWarning,
              'Failed to save Quote!',
              apiResponse.Payload
            );
          }
        },
        (err) => {
          this.isLoading = false;
          this.appService.showToastMessage(
            AppEnums.ToastTypeError,
            '',
            this.appService.processHttpErrors(err)
          );
        }
      );
    this.router.navigate(['/motor/list/motor-details', this.selectedQuoteId]);
  }

  setCompanyInformation() {
    if (this.currentRecord?.moreQuestions?.company != null) {
      this.isLoading = false;
      this.companyFormGroup = this._formBuilder.group({
        emailAddress: [
          this.currentRecord?.moreQuestions?.company?.emailAddress,
          Validators.email,
        ],
        name: [
          this.currentRecord?.moreQuestions?.company?.name,
          Validators.required,
        ],
        phoneNumber: [
          this.currentRecord?.moreQuestions?.company?.phoneNumber,
          Validators.required,
        ],
        kraPin: [
          this.currentRecord?.moreQuestions?.company?.kraPin,
          Validators.required,
        ],
        KraDoc: [
          this.currentRecord?.moreQuestions?.company?.KraDoc,
          Validators.required,
        ],
        incorporationCert: [
          this.currentRecord?.moreQuestions?.company?.incorporationCert,
          Validators.required,
        ],
        physicaladdress: [
          this.currentRecord?.moreQuestions?.company?.physicaladdress,
          Validators.required,
        ],
        postalAddress: [
          this.currentRecord?.moreQuestions?.company?.postalAddress,
          Validators.required,
        ],
        postalCode: [
          this.currentRecord?.moreQuestions?.company?.postalCode,
          Validators.required,
        ],
      });
    }
    this.isLoading = true;
  }

  setGetQuoteUrl() {
    let microService = '';
    switch (this.product) {
      case 'motor':
        microService = `${environment.MOTOR_API_URL}`;
        this.getQuoteInformation('adminlistmotorquote', microService);
        break;
      case 'health':
        microService = `${environment.MEDICAL_API_URL}`;
        this.getQuoteInformation('adminlistquote', microService);
        break;
      default:
        return;
    }
    this.isLoading = true;
  }

  getQuoteInformation(endpoint: string, microService: string) {
    if (this.selectedQuoteId != null) {
      this.appService
        .makePostRequest(`${microService}/${endpoint}`, {
          _id: this.selectedQuoteId,
        })
        .pipe(switchMap((res) => this.getQuoteQuestions(res, microService)))
        .subscribe((apiResponse) => {
          if (apiResponse.Status === 200) {
            this.currentRecord = apiResponse.Payload;
            this.setCompanyInformation();
          } else {
            this.appService.showToastMessage(
              AppEnums.ToastTypeWarning,
              apiResponse.Payload,
              "Could not process this quote's information"
            );
          }
          (error) => {
            this.appService.showToastMessage(
              AppEnums.ToastTypeError,
              '',
              this.appService.processHttpErrors(error)
            );
          };
        });
    }
  }

  getQuoteQuestions(
    res: ApiResponse,
    microService: string
  ): Observable<ApiResponse> {
    let q = res.Payload[0].questions_id;
    return this.appService.makePostRequest(
      `${microService}/admingetquestions`,
      {
        _id: q,
      }
    );
  }

  ngOnInit(): void {
    this.setGetQuoteUrl();
  }
}
